import type { ReactElement, ReactNode } from 'react';

import { usePersonalisierung } from '../personalisierung/personalisierungApi';
import { APP_VERSION } from '../version';
import { Markenlogo } from './Logo';

/**
 * Fußzeile auf allen Seiten (Änderungsanforderungen ÄA-2 und ÄA-5).
 *
 * Drei Spalten, oben ausgerichtet: links das Logo linksbündig, in der Mitte Urheber und Version
 * zentriert, rechts die Verweise rechtsbündig. Unterhalb von `sm` stapeln sie in der Reihenfolge
 * Logo → Urheber → Verweise und stehen dann **alle** linksbündig: Zentriert und rechtsbündig
 * untereinander sähe die schmale Ansicht zerfasert aus. Die Fußzeile steht ausdrücklich
 * **außerhalb** von `<main>` und ist die einzige `contentinfo`-Landmarke der Seite.
 *
 * **Sie erscheint auch vor der Anmeldung.** §9.1 zählt abschließend auf, was dort sichtbar sein
 * darf; Impressum und Datenschutz sind von „gegebenenfalls rechtliche Hinweise" gedeckt, die
 * Urheber- und Versionszeile geht darüber hinaus (ÄA-2).
 *
 * **Seit ÄA-5 fragt sie den Server ab.** ÄA-2 hatte das Gegenteil zugesichert; die Fortschreibung
 * ist dort dokumentiert. Abgefragt wird ausschließlich die Personalisierung — keine
 * Veranstaltung, kein Code, kein Status. Schlägt der Abruf fehl, bleibt es bei den
 * mitgelieferten Adressen; die Anmeldeseite hängt nicht davon ab.
 *
 * **Hier steht immer das mcikmVOTE-Markenlogo**, auch wenn ein eigenes Firmenlogo hinterlegt ist:
 * Das eigene Logo tritt im Kopf und über den Anmeldungen an die Stelle der Marke, die Fußzeile
 * weist dagegen die Anwendung selbst aus.
 */

const FIRMENSEITE = 'https://www.m-c-i.biz';

/** Mitgelieferte Adresse für „Kontakt & Feedback", solange keine eigene hinterlegt ist. */
const FEEDBACK_VORGABE = 'https://www.eifel-netz.de/kontakt/feedback-mcikmvote/';

function ExternerVerweis({ ziel, children }: { ziel: string; children: ReactNode }): ReactElement {
  return (
    <a href={ziel} target="_blank" rel="noopener noreferrer" className="text-akzent underline">
      {children}
    </a>
  );
}

export function Fusszeile(): ReactElement {
  const personalisierung = usePersonalisierung();
  const feedbackZiel =
    personalisierung.data?.feedbackUrl !== undefined && personalisierung.data.feedbackUrl !== ''
      ? personalisierung.data.feedbackUrl
      : FEEDBACK_VORGABE;

  return (
    <footer className="border-rand text-text-gedaempft mt-6 border-t px-4 py-4 text-sm sm:px-6">
      <div className="mx-auto grid max-w-5xl grid-cols-1 items-start gap-6 text-left sm:grid-cols-3">
        <div>
          <Markenlogo />
        </div>

        <div className="flex flex-col gap-1 sm:items-center sm:text-center">
          <p>
            Design &amp; Umsetzung:{' '}
            <ExternerVerweis ziel={FIRMENSEITE}>Gierden M-C-I</ExternerVerweis>
          </p>
          <p>Version: {APP_VERSION}</p>
        </div>

        <div className="flex flex-col gap-1 sm:items-end sm:text-right">
          <p>
            <ExternerVerweis ziel={feedbackZiel}>Kontakt &amp; Feedback</ExternerVerweis>
          </p>
          {/* Eigene Seiten der Anwendung, aber ebenfalls in einem neuen Tab: Ein versehentlicher
              Klick soll eine laufende Abstimmung nicht verlassen (§19.1, ÄA-2). */}
          <p>
            <ExternerVerweis ziel="/impressum">Impressum</ExternerVerweis>
          </p>
          <p>
            <ExternerVerweis ziel="/datenschutz">Datenschutz</ExternerVerweis>
          </p>
        </div>
      </div>
    </footer>
  );
}
